import { css } from '@emotion/css';
import { createSelector } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/features';
import { actions as notificationActions } from 'src/features/notification';
import { actions as userActions } from 'src/features/user';
import { User } from 'src/models/User';
import { AppIcon, featureTypes } from './AppIcon';
import { DummyIcon } from './DummyIcon';
import { NotificationCard } from './notifications/NotificationCard';
import { NotificationListItem } from './notifications/NotificationListItem';
import { createNotificationMessage } from './notifications/utils/createNotificationMessage';
import { PageButton } from './PageButton';
import { PageIndicator } from './PageIndicator';
import { Statusbar } from './Statusbar';
import { WelcomeAnimation } from './WelcomeAnimation';

interface HomeScreenProps {
  user: User;
}

const selector = createSelector(
  [(state: RootState) => state.user, (state: RootState) => state.notification],
  (user, notification) => {
    const { splashFinished, reportedWarningFinished, unlockFlags, page } = user;
    const { notifications, unread, notify } = notification;

    const isNotificationExists = notifications.length > 0;
    const firstNotification = isNotificationExists ? notifications[0] : undefined;
    const moreCount = notifications.length > 1 ? notifications.length - 1 : undefined;

    const showReaction = unlockFlags?.isDisplayEmoticon;
    const showResort = unlockFlags?.hasAoc && unlockFlags.isUnlockedResortAppNet;
    const showLifeSupports = unlockFlags?.isDisplayLifeSupport;

    const allFeatures = featureTypes.filter(item => {
      switch (item) {
        case 'reaction':
          return showReaction;
        case 'resort':
          return showResort;
        case 'life-supports':
          return showLifeSupports;
        default:
          return true;
      }
    });

    return {
      splashFinished,
      reportedWarningFinished,
      allFeatures,
      page,
      unread,
      notify,
      firstNotification,
      moreCount
    };
  }
);

export const HomeScreen: React.FC<HomeScreenProps> = ({ user }) => {
  const { t } = useTranslation(['notification', 'translation']);
  const {
    splashFinished,
    reportedWarningFinished,
    allFeatures,
    page,
    unread,
    notify,
    firstNotification,
    moreCount
  } = useSelector(selector);
  const dispatch = useDispatch();
  const [notificationCardVisible, setNotificationCardVisible] = useState(false);

  const isReported = user.screening === 'ng';

  useEffect(() => {
    dispatch(notificationActions.refreshNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (notify && splashFinished) {
      // 通報NGの場合は通報警告ダイアログを閉じてから通知を表示する
      if (!isReported || reportedWarningFinished) {
        const timer = setTimeout(() => {
          setNotificationCardVisible(true);
          dispatch(notificationActions.notificationNotified());
        }, 1000);
        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [
    splashFinished,
    isReported,
    reportedWarningFinished,
    notify,
    setNotificationCardVisible,
    dispatch
  ]);

  const handleDismiss = useCallback(() => {
    setNotificationCardVisible(false);
  }, [setNotificationCardVisible]);

  const handlePrevPageClick = () => {
    dispatch(userActions.setPage({ page: 1 }));
  };
  const handleNextPageClick = () => {
    dispatch(userActions.setPage({ page: 2 }));
  };

  const countPerPage = 9;
  const hasNextPage = allFeatures.length > countPerPage && page === 1;
  const hasPrevPage = allFeatures.length > countPerPage && page === 2;

  const features = allFeatures.slice((page - 1) * countPerPage, page * countPerPage);
  const paddedFeatures = features.concat(Array(countPerPage - features.length).fill(undefined));

  return (
    <section
      className={css`
        display: flex;
        flex-direction: column;
        background: #f5f1e3;
        border-radius: 80px;
        width: 90%;
        max-width: 480px;
        padding: 24px 8px 16px;
        position: relative;
        pointer-events: ${splashFinished ? 'inherit' : 'none'};
      `}
    >
      {notificationCardVisible && firstNotification && (
        <NotificationCard onDismiss={handleDismiss}>
          <NotificationListItem
            type={firstNotification.type}
            message={createNotificationMessage(firstNotification, t)}
            asCard={true}
            moreCount={moreCount}
          />
        </NotificationCard>
      )}

      <Statusbar user={user} unreadNotificationsExist={unread} splashFinished={!!splashFinished} />

      <div
        id={page === 1 ? 'top-page-1' : 'top-page-2'}
        className={css`
          flex: 1 1 auto;
          display: flex;
          flex-wrap: wrap;
          position: relative;
          opacity: ${splashFinished ? 1 : 0};
          visibility: ${splashFinished ? 'visible' : 'hidden'};
          transform: ${splashFinished ? 'none' : 'scale(0.98)'};
          transition: all 0.2s cubic-bezier(0.17, 0.89, 0.56, 1.72);
          padding: 4%;
          @media (min-width: 480px) {
            padding: 4% 8%;
          }
        `}
        role="tabpanel"
      >
        {paddedFeatures.map((feature, i) =>
          feature ? <AppIcon key={feature} feature={feature} /> : <DummyIcon key={`dummy-${i}`} />
        )}

        {/* "前のページへ" ボタン */}
        {hasPrevPage ? (
          <PageButton
            direction="left"
            onClick={handlePrevPageClick}
            style={{
              position: 'absolute',
              top: 'calc(50% - 22px)',
              left: 'calc(-4% - 12px)'
            }}
            aria-label={t('translation:aria_prev_page')}
          />
        ) : null}

        {/* "次のページへ" ボタン */}
        {hasNextPage ? (
          <PageButton
            direction="right"
            onClick={handleNextPageClick}
            style={{
              position: 'absolute',
              top: 'calc(50% - 22px)',
              right: 'calc(-4% - 12px)'
            }}
            aria-label={t('translation:aria_next_page')}
          />
        ) : null}
      </div>

      {allFeatures.length > 9 ? (
        <div
          className={css`
            flex: 0 0 auto;
            opacity: ${splashFinished ? 1 : 0};
            visibility: ${splashFinished ? 'visible' : 'hidden'};
            transform: ${splashFinished ? 'none' : 'scale(0.75)'};
            transition: all 0.2s cubic-bezier(0.17, 0.89, 0.56, 1.72);
          `}
        >
          <PageIndicator page={page} />
        </div>
      ) : null}

      {!splashFinished && <WelcomeAnimation />}
    </section>
  );
};
